import type { components } from '@scalty/contracts'

/** What a deployment is called where a customer can recognise it: its name and its address. */
export type DeploymentNames = Map<string, { name: string; slug: string }>

/**
 * A key's access, said in the customer's words wherever the key is shown — the
 * creation panel, the list and the inspector all read this one function.
 *
 * A deployment-scoped key used to read `Deployment dep_j6xmt3e2dcca12sm ·
 * invoke only`: the stored identifier, which names nothing a customer can see
 * (W8-DS-17 check 6, walked on dev 2026-09-21). It now carries the deployment's
 * own name and address. A key whose deployment has since been deleted says that
 * instead of printing the handle: the key stays bound to an id that no longer
 * resolves, and that is the fact worth telling.
 */
export function keyScopeLabel(
  scope: components['schemas']['ApiKeyScope'],
  projectId: string | null,
  deployments?: DeploymentNames,
): string {
  const access = scope.kind === 'deployment' ? deploymentAccess(scope.deploymentId, deployments) : 'Whole organisation'
  return projectId ? `${access} · restricted to project ${projectId}` : access
}

function deploymentAccess(deploymentId: string | null | undefined, deployments?: DeploymentNames): string {
  const known = deploymentId ? deployments?.get(deploymentId) : undefined
  if (known) return `${known.name} · /${known.slug} · invoke only`
  /* The names have not been read here, or the deployment is gone. Either way the
     stored handle is not shown: it tells the customer nothing. */
  return deployments ? 'One deployment · invoke only · that deployment has been deleted' : 'One deployment · invoke only'
}
